import { h } from 'preact';
import { useHtmlLang, useKeyboardEvents, useAllyFeatureListeners } from 'hooks';
import { useContext, useEffect } from 'preact/hooks';
import { sendPostMessage } from 'helpers/post-message.helper';
import { ApplicationContext, useApplicationSetup } from 'contexts/use-application-setup';
import { stateStore } from 'global/state.store';
import { useConnect } from 'helpers/use-connect-to-global-store';
import {
  INITIAL_PAGE_STATE,
  LOGIN_STATE,
  MANAGE_STATE,
  NEW_LOGIN_STATE,
} from 'constants/constants';
import { configStore } from 'global/config.store';
import { sessionStore } from 'global/session.store';
import { tokenStore } from 'global/token.store';
import { StatisticsProvider } from 'contexts/use-statistics';
import { FocusedElementsHandlerWrapper } from 'contexts/use-focused-element-reader';
import { ViolationsProvider } from 'contexts/use-violations';
import { Trigger } from 'types/trigger';
import {
  AllyFeaturesContext,
  AllyFeaturesProvider,
} from '../accessibility-features/use-ally-features';
import { useReaderGuidePosition } from '../accessibility-features/use-reader-guide-position';
import { MainWrapper } from '../main-wrapper/main-wrapper';
import { AccessibilityFeaturesProvider } from '../accessibility-features/use-accessibility-fetures';
import { LSTProvider } from '../../contexts/lst/lst-provider';
import { getInitialPageView } from './get-initial-page-view';
import { InitialPageView } from './initial-page-view';
import { Views } from './types';
import './initial-page.scss';

export const InitialPageWrapper = () => {
  const [page, setPage] = useConnect(stateStore.state);
  const [isLogged] = useConnect(sessionStore.isLogged$);
  const { allyFeaturesState } = useContext(AllyFeaturesContext);

  useHtmlLang();

  const config = useApplicationSetup();
  useAllyFeatureListeners(config.sendEvent, config.settings.current);
  useKeyboardEvents(config);
  useReaderGuidePosition(allyFeaturesState);

  const trigger = config?.trigger;
  const view = getInitialPageView({ page, trigger, isWidgetReady: config.isWidgetReady, isLogged });

  useEffect(() => {
    if (page !== INITIAL_PAGE_STATE && trigger === Trigger.Lst) {
      // we don't support login/manage screens for lst widget
      stateStore.state.next(INITIAL_PAGE_STATE);
    }
  }, [page, trigger]);

  // react on config updates and pass new values to the configStore
  useEffect(() => {
    configStore.config$.next(config);
  }, [config]);

  // This hook triggers only once peg widget initialization
  useEffect(() => {
    const unsubscribe = configStore.config$.subscribe((config) => {
      if (!config.tuningsFromServerInProgress) {
        const loggingState = config.tunings?.new_login ? NEW_LOGIN_STATE : LOGIN_STATE;
        const isHeadlessMode = config?.widgetLayout === 'headless';
        // In headless mode, if a stored token exists the user is likely already logged in.
        // Skip straight to MANAGE_STATE so Angular and Preact both start at the manage view,
        // avoiding a Login→Manage race that causes a Preact insertBefore NotFoundError.
        // If the token turns out to be invalid, handleInvalidToken() will redirect to login.
        const headlessState = tokenStore.hasToken() ? MANAGE_STATE : loggingState;
        const initialState = isHeadlessMode ? headlessState : INITIAL_PAGE_STATE;

        setPage(initialState);
        sendPostMessage({ action: 'appConfigLoaded' });
        unsubscribe();
      }
    });
  }, []);

  useEffect(() => {
    if (view === Views.SkeletonPage || isLogged) {
      config.loadAngularPart();
    }
  }, [view, isLogged]);

  return (
    <ApplicationContext.Provider value={config}>
      <LSTProvider>
        <StatisticsProvider>
          <AccessibilityFeaturesProvider>
            <ViolationsProvider>
              <MainWrapper>
                <FocusedElementsHandlerWrapper>
                  {/* Stable wrapper prevents Angular's ui-view replacement from invalidating
                        the insertBefore reference node Preact holds for InitialPageView updates. */}
                  <div>
                    <InitialPageView view={view} />
                  </div>
                  {/* Angular view */}
                  <div ui-view="" />
                  {/* ------------ */}
                </FocusedElementsHandlerWrapper>
              </MainWrapper>
            </ViolationsProvider>
          </AccessibilityFeaturesProvider>
        </StatisticsProvider>
      </LSTProvider>
    </ApplicationContext.Provider>
  );
};

export const InitialPage = () => (
  <AllyFeaturesProvider>
    <InitialPageWrapper />
  </AllyFeaturesProvider>
);
